import React, {useState} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import Icon from './Icon';
import cx from 'classnames';
import textProvider from '../texts/textProvider';
import {Popover, Tooltip}  from '@perion-undertone/ut-react-common';

const CLASS_NAMES = {
    CTC_WRAPPER: 'copy-to-clipboard-wrapper',
    CTC_POPOVER: 'copy-to-clipboard-popover',
    CTC_POPOVER_MENU: 'copy-to-clipboard-popover-menu',
    CTC_POPOVER_MENU_ITEM: 'copy-to-clipboard-popover-menu-item',
    SQUARED_ICON_WRAPPER: 'squared-icon-wrapper',
    ID_ICON_WRAPPER: 'id-icon-wrapper',
    COPY_ICON: 'copy-icon',
    COPY_TRIGGER: 'copy-to-clipboard-trigger',
    COPY_TXT: 'copy-txt'
};

const CONSTANT_TEXTS = {
    COPIED_MESSAGE: textProvider.getText('tooltip', 'copied')
};

const fallbackCopyToClipboard = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
};

const writeToClipboard = (copyValue) => {
    const text = String(copyValue ?? '');

    if (!text) {
        return Promise.resolve();
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
        return navigator.clipboard.writeText(text).catch(() => {
            fallbackCopyToClipboard(text);
        });
    }

    fallbackCopyToClipboard(text);
    return Promise.resolve();
};

const CopyToClipboard = (props) => {
    const {copy, noWrap, copyMessage, placement, icon, getCopyValue} = props;
    const [copyTarget, setCopyTarget] = useState('');

    const resolveCopyValue = () => {
        if (typeof getCopyValue === 'function') {
            return getCopyValue();
        }

        return props.copyValue;
    };

    const hasCopyAction = typeof getCopyValue === 'function' || props.copyValue;

    const copyMessageTrigger = (<>
        <Icon icon={icon || 'copy.svg'} hookId={props.hookId} />
        <span className={CLASS_NAMES.COPY_TXT}>{copy}</span>
    </>);

    const trigger = (<div className={CLASS_NAMES.COPY_TRIGGER}>
        {copyMessage ?
            <Tooltip appendToBody
                     placement="top"
                     trigger={<div className={CLASS_NAMES.COPY_TRIGGER}>{copyMessageTrigger}</div>}
            >
                {copyMessage}
            </Tooltip> :
            copyMessageTrigger
        }
        </div>);

    const copyToClipboard = (e, targetName) => {
        writeToClipboard(resolveCopyValue());
        setCopyTarget(targetName);
    };

    const getCopiedMessage = () => {
        return props.copiedMessage || CONSTANT_TEXTS.COPIED_MESSAGE;
    }

    return (
        <div className={CLASS_NAMES.CTC_WRAPPER}>
            {document.queryCommandSupported('copy') &&
                <div>
                    <div className={cx({[CLASS_NAMES.SQUARED_ICON_WRAPPER]: !noWrap}, CLASS_NAMES.ID_ICON_WRAPPER, CLASS_NAMES.COPY_ICON)}>
                        <Popover trigger={trigger}
                                 className={cx({[CLASS_NAMES.CTC_POPOVER]: !!hasCopyAction})}
                                 onClick={hasCopyAction ? (e) => copyToClipboard(e, props.copyTarget) : _.noop()}
                                 placement={placement}
                                 appendToBody
                                 basic={true}
                                 disabled={false}
                                 hookId="copy-to-clipboard-popover"
                        >
                            {hasCopyAction && <div>
                                {copyTarget === props.copyTarget ? getCopiedMessage() : ''}
                            </div>}
                        </Popover>
                    </div>
                </div>}
        </div>
    );
};

CopyToClipboard.propTypes = {
    copy: PropTypes.string,
    copyValue: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.string
    ]),
    copyTarget: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
    ]),
    noWrap: PropTypes.bool,
    hookId: PropTypes.string,
    copiedMessage: PropTypes.string,
    copyMessage: PropTypes.string,
    placement: PropTypes.string,
    icon: PropTypes.string,
    getCopyValue: PropTypes.func
};

CopyToClipboard.defaultProps = {
    noWrap: false,
    placement: 'bottom'
}

export default CopyToClipboard;